/**
 * RecommendedProducts — tops a product slider up with Search & Discovery
 * recommendations. Used by the PDP "Recommended products" section and by the
 * cart drawer's upsell slider (`snippets/cart-upsell.liquid`).
 *
 * The `recommendations` Liquid object is empty on a normal page render, so the
 * section can only see its curated product list. This element re-requests the
 * same section through the Product Recommendations API, where Liquid merges the
 * curated and recommended lists, and swaps the rendered result in.
 *
 * The section only wraps itself in this element when a fetch could change the
 * outcome (S&D enabled, curated list shorter than the fill threshold), so its
 * presence in the DOM is the signal to fetch.
 *
 * Rather than morphing, the content container's children are replaced outright:
 * that tears down and re-creates the inner <card-carousel>, so its click and
 * scroll listeners are rebound by the normal custom-element lifecycle.
 *
 * Surviving cart morphs: the cart drawer is morphed after every cart change, and
 * that morph keeps this element but resets its attributes and children to the
 * server's first-pass state (`data-recommendations-performed` stripped, seed
 * product possibly changed, placeholder content back). `attributeChangedCallback`
 * picks that up and reloads. It waits a microtask first so every attribute the
 * morph touches has settled before the endpoint and cache key are read. Responses
 * are cached per endpoint + cart fingerprint so a quantity change swaps the same
 * markup back in synchronously instead of hiding the slider and refetching.
 *
 * DOM contract:
 *   data-url                        — recommendations endpoint, already carrying `?limit=`
 *   data-section-id                 — section id, for the Section Rendering API
 *   data-product-id                 — the product to recommend against
 *   data-intent                     — "related" | "complementary"
 *   data-cart-key                   — optional cart fingerprint; part of the cache key
 *   data-recommendations-performed  — "true" once the merged markup is in place
 *   [data-recommended-products-content] — the container whose children get replaced
 */

import { requestIdleCallback } from "@theme/utilities";

const CONTENT_SELECTOR = "[data-recommended-products-content]";

/**
 * Rendered responses keyed by endpoint + cart fingerprint, shared across instances.
 * @type {Map<string, string>}
 */
const responseCache = new Map();

class RecommendedProducts extends HTMLElement {
  static observedAttributes = ["data-product-id", "data-recommendations-performed"];

  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: "0px 0px 400px 0px" }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Whether a reload is already queued for the current microtask. */
  #reloadQueued = false;

  connectedCallback() {
    if (this.dataset.recommendationsPerformed === "true") return;

    // A hidden element has no layout box, so IntersectionObserver would never
    // fire for it. That only happens when there is nothing to show yet, which
    // is also when the fetch matters most — so fall back to idle time.
    if (this.hidden) {
      requestIdleCallback(() => this.#loadRecommendations());
    } else {
      this.#intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    // Initial attributes (and upgrades) go through connectedCallback; only later
    // changes, i.e. a morph resetting or re-seeding the element, matter here.
    if (oldValue === null || oldValue === newValue || this.#reloadQueued) return;

    this.#reloadQueued = true;
    queueMicrotask(() => {
      this.#reloadQueued = false;
      if (!this.isConnected) return;
      this.#loadRecommendations();
    });
  }

  async #loadRecommendations() {
    // Set by our own swap; also true when the server already rendered the merged list.
    if (this.dataset.recommendationsPerformed === "true") return;

    const { url, sectionId, productId, intent, cartKey } = this.dataset;

    if (!url || !sectionId || !productId) {
      this.#reveal();
      return;
    }

    const endpoint = `${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const cacheKey = `${endpoint}|${cartKey ?? ""}`;

    const cached = responseCache.get(cacheKey);
    if (cached) {
      this.#swapContent(cached, sectionId);
      return;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(endpoint, { signal: this.#activeFetch.signal });

      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const html = await response.text();
      responseCache.set(cacheKey, html);
      this.#swapContent(html, sectionId);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;

      // The Theme Editor places sections in the DOM whose section_id the
      // Section Rendering API does not know about; nothing to report there.
      if (!window.Shopify?.designMode) {
        console.error("Recommended products:", error instanceof Error ? error.message : error);
      }

      // Fall back to whatever the server already rendered rather than leaving
      // a curated slider permanently hidden behind a failed request.
      this.#reveal();
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {string} html - The Section Rendering API response
   * @param {string} sectionId
   */
  #swapContent(html, sectionId) {
    const content = this.querySelector(CONTENT_SELECTOR);
    const incoming = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById(`shopify-section-${sectionId}`)
      ?.querySelector(CONTENT_SELECTOR);

    if (!content || !incoming) {
      this.#reveal();
      return;
    }

    this.dataset.recommendationsPerformed = "true";

    // Liquid renders an empty container when the merged list falls below the
    // configured minimum — in that case the section stays hidden.
    //
    // Unhide *before* inserting: <card-carousel> binds its scroller in
    // connectedCallback and ignores any scroller under a `[hidden]` ancestor
    // (so tabbed panels can hide inactive scrollers). Inserting while this
    // element is still hidden would leave the carousel with no scroller and its
    // arrows permanently disabled. Both statements run in the same task, so
    // nothing paints in between.
    this.hidden = incoming.children.length === 0;
    content.replaceChildren(...incoming.childNodes);
  }

  #reveal() {
    const content = this.querySelector(CONTENT_SELECTOR);
    this.hidden = !content || content.children.length === 0;

    // The fallback markup was parsed while this element was hidden, so any
    // <card-carousel> in it connected without a scroller (see #swapContent).
    // Now that it is visible, let it rebind.
    if (!this.hidden) {
      this.querySelectorAll("card-carousel").forEach((carousel) => {
        /** @type {{ refresh?: () => void }} */ (carousel).refresh?.();
      });
    }
  }
}

if (!customElements.get("recommended-products")) {
  customElements.define("recommended-products", RecommendedProducts);
}
